import { Component, type ErrorInfo, type ReactNode } from 'react';

import { logger } from '@/lib/logger';
import { Sentry } from '@/lib/sentry';

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
};

type State = {
  hasError: boolean;
  error?: Error;
};

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error('react error boundary caught', {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
    Sentry.captureException(error);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-2 p-8 text-center">
          <h1 className="text-xl font-semibold">Something went wrong.</h1>
          <p className="text-sm text-gray-600">
            The error has been reported. Please refresh the page.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
